import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress } from '@material-ui/core'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import Loading from '../../../../molecules/Loading'
import EmptySection from '../../../../atoms/EmptySection'
import HierarchicalOpalGrid from '../../../DenaliDataPipeline/report/HierarchicalOpalGrid'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { buildTanstackColumns, COLUMNS } from './columnDefs'
import './gridDocuments.css'
import {
  DOCUMENTS_GRID_CLASS,
  documentsCellStyle,
  documentsGridStyle,
  documentsHeaderStyle
} from './gridStyles'
import { PAGE_SIZE_OPTIONS } from './constants'
import { toGridSortState } from './queryBuilders'
import UserLoginActivityProvider, { useUserLoginActivityContext } from './UserLoginActivityProvider'
import UserLoginActivityToolbar from './UserLoginActivityToolbar'

const gridAreaStyle = {
  position: 'relative',
  minHeight: '120px'
}

const fetchOverlayStyle = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  pointerEvents: 'none'
}

function GridFetchOverlay ({ show }) {
  if (!show) {
    return null
  }
  return (
    <div style={fetchOverlayStyle} aria-busy='true' aria-live='polite'>
      <CircularProgress color='primary' size={36} />
    </div>
  )
}

GridFetchOverlay.propTypes = {
  show: PropTypes.bool
}

function UserLoginActivityContent () {
  const { formatter } = useFormattingContext()
  const {
    config,
    rows,
    total,
    isLoading,
    isFetching,
    pageState,
    sortState,
    onPageChange,
    onSortChange
  } = useUserLoginActivityContext()

  const showUserAgent = config?.showUserAgent ?? true
  const columnDefs = useMemo(
    () => (showUserAgent ? COLUMNS : COLUMNS.filter(c => c.name !== 'userAgent')),
    [showUserAgent]
  )
  const tanstackColumns = useMemo(
    () => buildTanstackColumns(columnDefs, formatter),
    [columnDefs, formatter]
  )
  const gridSortState = useMemo(
    () => toGridSortState(sortState),
    [sortState]
  )

  const isInitialLoad = isLoading && rows.length === 0
  const showFetchOverlay = isFetching && !isInitialLoad

  const renderGridArea = () => {
    if (isInitialLoad) {
      return <Loading />
    }
    if (!isFetching && total === 0) {
      return (
        <EmptySection
          title='No login activity'
          description='No users with login activity were found.'
        />
      )
    }
    return (
      <div style={gridAreaStyle}>
        <HierarchicalOpalGrid
          className={`separated-rows ${DOCUMENTS_GRID_CLASS}`}
          style={documentsGridStyle}
          componentStyles={{
            header: documentsHeaderStyle,
            cell: documentsCellStyle
          }}
          data={rows}
          columns={tanstackColumns}
          isFetching={isFetching}
          pageState={pageState}
          sortState={gridSortState}
          onPageChange={onPageChange}
          onSortChange={onSortChange}
          manualPagination
          manualSorting
          totalRows={total}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <GridFetchOverlay show={showFetchOverlay} />
      </div>
    )
  }

  return (
    <div style={{ padding: '8px 12px' }}>
      <UserLoginActivityToolbar />
      {renderGridArea()}
    </div>
  )
}

function UserLoginActivityRenderer ({ config }) {
  return (
    <FadeIn>
      <UserLoginActivityProvider config={config}>
        <UserLoginActivityContent />
      </UserLoginActivityProvider>
    </FadeIn>
  )
}

UserLoginActivityRenderer.propTypes = {
  config: PropTypes.object
}

export default UserLoginActivityRenderer
